<script setup>
import { ref, computed, watch, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { debounce } from '@chatwoot/utils';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  supplier: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'close']);

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const contactResults = ref([]);
const searchingContacts = ref(false);

const formData = reactive({
  name: '',
  ownerId: '',
  contactId: '',
  contactName: '',
  userIds: [],
});

const agents = computed(() => store.getters['agents/getAgents'] || []);

const selectedMemberNames = computed(() =>
  formData.userIds.map(
    id => agents.value.find(agent => agent.id === id)?.name ?? ''
  )
);

const memberMenuItems = computed(() =>
  agents.value
    .filter(agent => !formData.userIds.includes(agent.id))
    .map(agent => ({
      label: agent.name,
      value: agent.id,
      action: 'select',
      thumbnail: {
        name: agent.name,
        src: agent.thumbnail || agent.avatar_url || '',
      },
    }))
);

const contactOptions = computed(() => {
  const options = contactResults.value.map(contact => ({
    value: contact.id,
    label: contact.email
      ? `${contact.name} (${contact.email})`
      : contact.name,
  }));

  if (
    formData.contactId &&
    !options.some(option => option.value === formData.contactId)
  ) {
    options.unshift({
      value: formData.contactId,
      label: formData.contactName || String(formData.contactId),
    });
  }

  return options;
});

const contactEmptyState = computed(() =>
  searchingContacts.value
    ? t('SUPPLIERS.LOADING')
    : t('SUPPLIERS.FORM.CONTACT_EMPTY')
);

const isEditMode = computed(() => !!props.supplier);

const dialogTitle = computed(() =>
  isEditMode.value
    ? t('SUPPLIERS.FORM.EDIT_TITLE')
    : t('SUPPLIERS.FORM.CREATE_TITLE')
);

const confirmButtonLabel = computed(() =>
  isEditMode.value ? t('SUPPLIERS.FORM.UPDATE') : t('SUPPLIERS.FORM.CREATE')
);

const isFormValid = computed(() => formData.name.trim().length > 0);

const resetForm = () => {
  Object.assign(formData, {
    name: '',
    ownerId: '',
    contactId: '',
    contactName: '',
    userIds: [],
  });
  contactResults.value = [];
};

const populateForm = supplier => {
  Object.assign(formData, {
    name: supplier.name || '',
    ownerId: supplier.ownerId || supplier.owner_id || '',
    contactId: supplier.contactId || supplier.contact_id || '',
    contactName: supplier.contact?.name || '',
    userIds: (supplier.userIds || supplier.user_ids || []).map(Number),
  });
};

watch(
  () => props.supplier,
  newSupplier => {
    if (newSupplier) populateForm(newSupplier);
  },
  { immediate: true }
);

const searchContacts = debounce(
  async query => {
    if (!query || query.length < 2) {
      contactResults.value = [];
      return;
    }
    searchingContacts.value = true;
    try {
      const {
        data: { payload },
      } = await ContactAPI.search(query);
      contactResults.value = payload || [];
    } catch {
      contactResults.value = [];
    } finally {
      searchingContacts.value = false;
    }
  },
  300,
  false
);

const onContactSelect = value => {
  formData.contactId = value || '';
  if (!value) {
    formData.contactName = '';
    return;
  }
  const contact = contactResults.value.find(item => item.id === value);
  formData.contactName =
    contact?.name ||
    contactOptions.value.find(option => option.value === value)?.label ||
    '';
};

const handleMemberAdd = ({ value }) => {
  const id = Number(value);
  if (!formData.userIds.includes(id)) {
    formData.userIds.push(id);
  }
};

const handleMemberRemove = index => {
  formData.userIds.splice(index, 1);
};

const open = () => {
  if (!props.supplier) resetForm();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleDialogClose = () => {
  emit('close');
};

const handleSave = () => {
  if (!isFormValid.value) return;

  const payload = {
    name: formData.name.trim(),
    owner_id: formData.ownerId || null,
    contact_id: formData.contactId || null,
    user_ids: formData.userIds,
  };

  if (isEditMode.value) {
    payload.id = props.supplier.id;
  }

  emit('save', payload);
};

onMounted(async () => {
  if (!agents.value.length) await store.dispatch('agents/get');
});

defineExpose({
  open,
  close,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :confirm-button-label="confirmButtonLabel"
    :cancel-button-label="t('SUPPLIERS.FORM.CANCEL')"
    :disable-confirm-button="!isFormValid"
    :is-loading="isLoading"
    width="lg"
    @confirm="handleSave"
    @close="handleDialogClose"
  >
    <div class="flex flex-col gap-4 p-1">
      <Input
        v-model="formData.name"
        :label="t('SUPPLIERS.FORM.FIELDS.NAME')"
        :placeholder="t('SUPPLIERS.FORM.PLACEHOLDERS.NAME')"
        required
      />

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">{{
          t('SUPPLIERS.FORM.FIELDS.OWNER')
        }}</span>
        <select
          v-model="formData.ownerId"
          class="h-10 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12"
        >
          <option value="">
            {{ t('SUPPLIERS.FORM.PLACEHOLDERS.OWNER') }}
          </option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
      </label>

      <div class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">{{
          t('SUPPLIERS.FORM.FIELDS.CONTACT')
        }}</span>
        <ComboBox
          use-api-results
          :model-value="formData.contactId"
          :options="contactOptions"
          :empty-state="contactEmptyState"
          :search-placeholder="t('SUPPLIERS.FORM.PLACEHOLDERS.CONTACT')"
          :placeholder="t('SUPPLIERS.FORM.PLACEHOLDERS.CONTACT')"
          class="[&>div>button]:bg-n-alpha-black2"
          @update:model-value="onContactSelect"
          @search="searchContacts"
        />
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">{{
          t('SUPPLIERS.FORM.FIELDS.USERS')
        }}</span>
        <div
          class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
        >
          <TagInput
            :model-value="selectedMemberNames"
            :placeholder="t('SUPPLIERS.FORM.PLACEHOLDERS.USERS')"
            :menu-items="memberMenuItems"
            show-dropdown
            skip-label-dedup
            @add="handleMemberAdd"
            @remove="handleMemberRemove"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
